import { cn } from "@/lib/utils";
import { CompanyDetailsSection } from "@/components/corporate/organizations/page/CompanyDetailsSection";
import { LanguageSection } from "@/components/corporate/organizations/page/LanguageSection";
import { AppAddressSection } from "@/components/corporate/organizations/page/AppAddressSection";
import { BrandingSection } from "@/components/corporate/organizations/page/BrandingSection";
import { FeaturesSection } from "@/components/corporate/organizations/page/FeaturesSection";
import type { SettingsSectionKey } from "@/components/corporate/organizations/page/SectionFrame";
import type { Organization } from "@/types/organizations";

export const SETTINGS_SECTIONS: { key: SettingsSectionKey; label: string }[] = [
  { key: "details", label: "Company details" },
  { key: "language", label: "Language" },
  { key: "app", label: "App & web address" },
  { key: "branding", label: "Branding" },
  { key: "features", label: "Features & access" },
];

export const sectionLabel = (key: SettingsSectionKey) => SETTINGS_SECTIONS.find((s) => s.key === key)?.label ?? key;

export const isSettingsSection = (value: string): value is SettingsSectionKey =>
  SETTINGS_SECTIONS.some((s) => s.key === value);

interface OrgSettingsTabProps {
  organization: Organization;
  section: SettingsSectionKey;
  /** The page decides (it asks before leaving a section with unsaved edits). */
  onSelectSection: (section: SettingsSectionKey, opener?: HTMLElement) => void;
  /** The section with unsaved edits, if any (shown with a dot). */
  dirtySection: SettingsSectionKey | null;
  onDirtyChange: (section: SettingsSectionKey, dirty: boolean) => void;
}

/**
 * Settings — one place per setting: a section list on the left and the open section on the right.
 * Each section has its own Save; only the open section is mounted.
 */
export function OrgSettingsTab({ organization, section, onSelectSection, dirtySection, onDirtyChange }: OrgSettingsTabProps) {
  const report = (dirty: boolean) => onDirtyChange(section, dirty);

  return (
    <div className="grid gap-4 md:grid-cols-[220px_minmax(0,1fr)]" data-testid="org-settings">
      <nav aria-label="Settings sections" className="flex flex-row gap-1 overflow-x-auto md:flex-col">
        {SETTINGS_SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            data-testid={`org-settings-nav-${s.key}`}
            aria-current={s.key === section ? "page" : undefined}
            onClick={(event) => onSelectSection(s.key, event.currentTarget)}
            className={cn(
              "flex items-center justify-between gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-left text-[13.5px] font-semibold transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-accent",
              s.key === section ? "bg-ds-panel text-ds-ink shadow-ds" : "text-ds-ink2 hover:bg-ds-sunk hover:text-ds-ink",
            )}
          >
            {s.label}
            {dirtySection === s.key && (
              <span className="h-2 w-2 rounded-full bg-ds-warn" title="Unsaved changes" aria-label="Unsaved changes" />
            )}
          </button>
        ))}
      </nav>
      <div className="min-w-0">
        {section === "details" && <CompanyDetailsSection organization={organization} onDirtyChange={report} />}
        {section === "language" && <LanguageSection organization={organization} onDirtyChange={report} />}
        {section === "app" && <AppAddressSection organization={organization} onDirtyChange={report} />}
        {section === "branding" && <BrandingSection organization={organization} onDirtyChange={report} />}
        {section === "features" && <FeaturesSection organization={organization} onDirtyChange={report} />}
      </div>
    </div>
  );
}

export default OrgSettingsTab;
